// src/pages/AdminPage/ModelsTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Button,
  Drawer,
  TextField,
  MenuItem,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Cpu,
  Plus,
  X,
  CheckCircle2,
  Server,
  Pencil,
  Trash2,
  Activity,
  Gauge,
  KeyRound,
  Globe,
  Lock,
} from 'lucide-react';
import {
  listAdminModels,
  addModel,
  updateModel,
  deleteModel,
  setModelEnabled,
  DuplicateModelError,
  type AdminModel,
  type ModelApiType,
} from 'services/demoApi/modelsAdminApi';

const API_TYPES: ModelApiType[] = ['OpenAI-compatible', 'vLLM', 'Triton'];

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}М`;
  if (n >= 1000) return `${Math.round(n / 1000)}К`;
  return String(n);
}

const drawerFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

export default function ModelsTab() {
  const [models, setModels] = useState<AdminModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // null = режим добавления, string = оригинальное имя редактируемой модели
  const [editingOriginalName, setEditingOriginalName] = useState<string | null>(null);

  // Форма подключения/редактирования
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('8000');
  const [apiType, setApiType] = useState<ModelApiType>('OpenAI-compatible');
  const [external, setExternal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const refresh = useCallback(() => {
    listAdminModels().then((items) => {
      setModels(items);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Сводные метрики по подключённым моделям (FR-3.3)
  const activeModel = models.find((m) => m.enabled) ?? null;
  const avgLatency = models.length
    ? Math.round(models.reduce((sum, m) => sum + m.latencyMs, 0) / models.length)
    : 0;

  const toggleModel = async (model: AdminModel) => {
    const next = !model.enabled;
    // Активна только одна модель: включение делает её единственной активной,
    // выключение снимает активность со всех.
    setModels((prev) => prev.map((m) => ({ ...m, enabled: next && m.id === model.id })));
    await setModelEnabled(model.name, next);
  };

  const resetForm = () => {
    setName('');
    setHost('');
    setPort('8000');
    setApiType('OpenAI-compatible');
    setExternal(false);
    setApiKey('');
    setFormError(null);
    setSuccess(false);
    setEditingOriginalName(null);
  };

  const openEditDrawer = (model: AdminModel) => {
    setEditingOriginalName(model.name);
    setName(model.name);
    setHost(model.host);
    setPort(String(model.port));
    setApiType(model.apiType);
    setExternal(model.external);
    setApiKey(model.apiKey);
    setFormError(null);
    setSuccess(false);
    setDrawerOpen(true);
  };

  const handleDelete = async (model: AdminModel) => {
    await deleteModel(model.name);
    refresh();
  };

  const submit = async () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('Укажите название модели');
      return;
    }
    if (external) {
      if (!host.trim()) {
        setFormError('Укажите адрес или URL внешнего API');
        return;
      }
    } else if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(host.trim())) {
      setFormError('Укажите корректный IP-адрес (например 10.12.4.30)');
      return;
    }
    const portNum = Number(port);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      setFormError('Порт должен быть числом от 1 до 65535');
      return;
    }
    if (external && !apiKey.trim()) {
      setFormError('Укажите API-ключ для внешней модели');
      return;
    }

    if (editingOriginalName !== null) {
      // Режим редактирования: без «Проверки соединения», просто сохранить
      setTesting(true);
      try {
        await updateModel(editingOriginalName, {
          name,
          host,
          port: portNum,
          apiType,
          external,
          apiKey,
        });
        refresh();
        setDrawerOpen(false);
        resetForm();
      } catch (e) {
        setFormError(e instanceof DuplicateModelError ? e.message : 'Не удалось сохранить модель');
      } finally {
        setTesting(false);
      }
    } else {
      // Режим добавления: проверка соединения + success-плашка
      setTesting(true);
      try {
        await addModel({ name, host, port: portNum, apiType, external, apiKey });
        setSuccess(true);
        refresh();
        setTimeout(() => {
          setDrawerOpen(false);
          resetForm();
        }, 1200);
      } catch (e) {
        setFormError(e instanceof DuplicateModelError ? e.message : 'Не удалось подключить модель');
      } finally {
        setTesting(false);
      }
    }
  };

  const cardSx = {
    background: '#FFFFFF',
    border: '1px solid rgba(20,116,255,0.10)',
    boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
    borderRadius: 'var(--radius-lg)',
  };

  return (
    <Box>
      {/* Действия */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Подключённые LLM-модели и их использование
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={15} />}
          onClick={() => {
            resetForm();
            setEditingOriginalName(null);
            setDrawerOpen(true);
          }}
          sx={{
            bgcolor: 'var(--color-primary)',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'none',
            px: 2,
            py: 1,
            '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
          }}
        >
          Подключить модель
        </Button>
      </Box>

      {/* Сводные метрики (FR-3.3) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3.5,
        }}
      >
        {[
          {
            icon: <Cpu size={16} />,
            label: 'Подключено моделей',
            value: isLoading ? '—' : String(models.length),
          },
          {
            icon: <Activity size={16} />,
            label: 'Активная модель',
            value: isLoading ? '—' : (activeModel?.name ?? 'Нет активной'),
            accent: activeModel ? '#059669' : undefined,
            small: true,
          },
          {
            icon: <Gauge size={16} />,
            label: 'Средняя задержка',
            value: isLoading ? '—' : `${avgLatency} мс`,
          },
        ].map((kpi) => (
          <Box key={kpi.label} sx={{ ...cardSx, p: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background:
                    'linear-gradient(135deg, rgba(20,116,255,0.10), rgba(20,116,255,0.08))',
                  border: '1px solid rgba(20,116,255,0.14)',
                  color: kpi.accent ?? 'var(--color-primary)',
                  flexShrink: 0,
                }}
              >
                {kpi.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-muted)',
                  lineHeight: 1.2,
                }}
              >
                {kpi.label}
              </Typography>
            </Box>
            <Typography
              noWrap={kpi.small}
              title={kpi.small ? kpi.value : undefined}
              sx={{
                fontSize: kpi.small ? 16 : 24,
                fontWeight: 780,
                color: kpi.accent ?? 'var(--color-text)',
              }}
            >
              {kpi.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Карточки моделей */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 2,
        }}
      >
        {isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} sx={{ ...cardSx, p: 2.5 }}>
              <Skeleton height={90} />
            </Box>
          ))}
        {!isLoading &&
          models.map((model) => (
            <Box key={model.id} sx={{ ...cardSx, p: 2.5, opacity: model.enabled ? 1 : 0.6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 'var(--radius-md)',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(20,116,255,0.10), rgba(20,116,255,0.08))',
                    border: '1px solid rgba(20,116,255,0.14)',
                    color: 'var(--color-primary)',
                  }}
                >
                  <Cpu size={17} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 750 }} noWrap>
                    {model.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: 'var(--color-text-muted)',
                      fontFamily: 'monospace',
                    }}
                    noWrap
                  >
                    {model.host}:{model.port} · {model.apiType}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Box
                    component="button"
                    aria-label={`Редактировать ${model.name}`}
                    onClick={() => openEditDrawer(model)}
                    sx={{
                      width: 30,
                      height: 30,
                      border: 'none',
                      bgcolor: 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-text-muted)',
                      '&:hover': {
                        bgcolor: 'rgba(20,116,255,0.07)',
                        color: 'var(--color-primary)',
                      },
                    }}
                  >
                    <Pencil size={14} />
                  </Box>
                  <Box
                    component="button"
                    aria-label={`Удалить ${model.name}`}
                    onClick={() => void handleDelete(model)}
                    sx={{
                      width: 30,
                      height: 30,
                      border: 'none',
                      bgcolor: 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-text-muted)',
                      '&:hover': { bgcolor: 'rgba(220,38,38,0.07)', color: '#dc2626' },
                    }}
                  >
                    <Trash2 size={14} />
                  </Box>
                  <Switch
                    size="small"
                    checked={model.enabled}
                    onChange={() => void toggleModel(model)}
                    inputProps={{ 'aria-label': `Включить ${model.name}` }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: model.enabled ? '#059669' : '#9ca3af',
                      boxShadow: model.enabled ? '0 0 0 3px rgba(5,150,105,0.18)' : 'none',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 650,
                      color: model.enabled ? '#059669' : 'var(--color-text-muted)',
                    }}
                  >
                    {model.enabled ? 'Online' : 'Отключена'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.85,
                    py: 0.25,
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    bgcolor: model.external ? 'rgba(20,116,255,0.08)' : 'rgba(0,0,0,0.05)',
                    color: model.external ? 'var(--color-primary)' : 'var(--color-text-muted)',
                  }}
                >
                  {model.external ? <Globe size={11} /> : <Lock size={11} />}
                  {model.external ? 'Внешняя' : 'Внутренняя'}
                </Box>
                <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  Latency: {model.latencyMs} мс
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  30 дней: {fmtTokens(model.tokens30d)} токенов
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>

      {/* Drawer подключения модели (FR-3.2) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          if (!testing) setDrawerOpen(false);
        }}
        slotProps={{ paper: { sx: { width: { xs: '100vw', sm: 420 }, p: 3 } } }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 750 }}>
            {editingOriginalName !== null ? 'Редактирование модели' : 'Подключение модели'}
          </Typography>
          <Box
            component="button"
            aria-label="Закрыть"
            onClick={() => {
              if (!testing) setDrawerOpen(false);
            }}
            sx={{
              width: 30,
              height: 30,
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-muted)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            <X size={16} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            label="Название модели"
            value={name}
            sx={drawerFieldSx}
            placeholder="Например, Qwen3-32B"
            onChange={(e) => setName(e.target.value)}
          />

          {/* Тип размещения: внутренняя (в контуре) или внешняя (внешний провайдер) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              px: 1.75,
              py: 1.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ color: external ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                {external ? <Globe size={16} /> : <Lock size={16} />}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 650 }}>
                  {external ? 'Внешняя модель' : 'Внутренняя модель'}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  {external
                    ? 'Внешний провайдер, нужен API-ключ'
                    : 'Развёрнута во внутреннем контуре'}
                </Typography>
              </Box>
            </Box>
            <Switch
              size="small"
              checked={external}
              onChange={(e) => setExternal(e.target.checked)}
              inputProps={{ 'aria-label': 'Внешняя модель' }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 1.5 }}>
            <TextField
              size="small"
              label={external ? 'Адрес / URL' : 'IP-адрес'}
              value={host}
              sx={drawerFieldSx}
              placeholder={external ? 'api.provider.com' : '10.12.4.30'}
              onChange={(e) => setHost(e.target.value)}
            />
            <TextField
              size="small"
              label="Порт"
              value={port}
              sx={drawerFieldSx}
              onChange={(e) => setPort(e.target.value)}
            />
          </Box>
          <TextField
            select
            size="small"
            label="Тип API"
            value={apiType}
            sx={drawerFieldSx}
            onChange={(e) => setApiType(e.target.value as ModelApiType)}
          >
            {API_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          {external && (
            <TextField
              size="small"
              type="password"
              label="API-ключ"
              value={apiKey}
              sx={drawerFieldSx}
              placeholder="sk-…"
              autoComplete="off"
              onChange={(e) => setApiKey(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <KeyRound
                      size={15}
                      style={{ marginRight: 8, color: 'var(--color-text-muted)', flexShrink: 0 }}
                    />
                  ),
                },
              }}
            />
          )}

          {formError && (
            <Typography sx={{ fontSize: 12.5, color: '#dc2626' }}>{formError}</Typography>
          )}

          {testing && editingOriginalName === null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                bgcolor: 'rgba(20,116,255,0.06)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <CircularProgress size={15} />
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 650 }}>
                  Проверка соединения…
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  {host}:{port} — handshake, проверка совместимости API
                </Typography>
              </Box>
            </Box>
          )}

          {success && editingOriginalName === null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                bgcolor: 'rgba(5,150,105,0.08)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <CheckCircle2 size={16} style={{ color: '#059669' }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: '#059669' }}>
                Соединение установлено. Модель подключена.
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={testing || success}
            startIcon={
              testing ? <CircularProgress size={14} color="inherit" /> : <Server size={15} />
            }
            sx={{
              mt: 1,
              bgcolor: 'var(--color-primary)',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'none',
              py: 1.1,
              '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
            }}
          >
            {editingOriginalName !== null
              ? testing
                ? 'Сохранение…'
                : 'Сохранить'
              : 'Проверить и подключить'}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
